import moment from "moment";
import { TFunction } from "i18next";

// ?? The functions below that validate / manipulate date objects / strings are no longer used by the DatePicker
// ?? and can probably be removed once we are happy the changes are fine. (12/03/21)
interface DateObject {
	day: string;
	month: string;
	year: string;
}

// Regex to validate an ISO string
const rgxValidator =
	/^([0-9]{4}-(((0[1-9]|1[0-2])-([0][1-9]|[1-2][0-9]|3[0-1]|))|(-)))$/;
// const rgxValidator = /^([0-9]{4}-(0[1-9]|1[0-2]|)-([0][1-9]|[1-2][0-9]|3[0-1]|))$/; // Old one that failed on some occasions.

// The ISO formats we support
const validIsoFormats = ["YYYY--", "YYYY-MM-", "YYYY-MM-DD"];

/**
 * Validates an iso string, valid formats include: YYYY--, YYYY-MM-, YYYY-MM-DD (and empty strings)
 */
export const validateISO = (iso: string): boolean => {
	// Basic validation to check the format of the
	// iso is correct before we split it
	if (typeof iso !== "string" || !rgxValidator.test(iso)) return false;

	const splitISO = iso.split("-");

	// Check which iso format to use.
	const dayBlank = splitISO[2] === "" ? true : false;
	const monthBlank = splitISO[1] === "" ? true : false;
	let myNum = 2;
	if (dayBlank) myNum = 1;
	if (monthBlank && dayBlank) myNum = 0;

	// moment is used to check date validity rather than format
	return moment(iso, validIsoFormats[myNum], true).isValid();
};

/**
 * Validates an DateObjectType, empty dates are valid
 */
export const validateDateObject = (dateObj: DateObject): boolean => {
	const { day, month, year } = dateObj;

	// Empty objects are treated as valid
	if (!year && !month && !day) return true;

	if ((day && !month) || (month && !year)) return false;

	// Build date string
	const output = createDateString(year, month, day);

	// Validate output
	return validateISO(output);
};

/**
 * Takes an Axiell ISO string and returns an object { day, month, year }
 * or null if the date is invalid
 */
export function isoToObject(iso: string): DateObject | null {
	if (!validateISO(iso)) return null;

	const splitISO = iso.split("-");

	const output = {
		day: splitISO[2] || "",
		month: splitISO[1] || "",
		year: splitISO[0],
	};

	return output;
}

/**
 * Takes a DateObjectType and returns an ISO string
 * or returns null if the object doesn't contain
 * a valid date - YYYY--, YYYY-MM-, YYYY-MM-DD
 * Note: An empty object is considered valid
 */
export function objectToIso(dateObj: DateObject): string | null {
	const { day, month, year } = dateObj;

	if (!validateDateObject(dateObj)) return null;

	// Empty objects are treated as valid
	if (!year && !month && !day) return "";

	return createDateString(year, month, day);
}

/**
 * Create a date string in iso format
 */
function createDateString(year: string, month?: string, day?: string) {
	// Create output ISO. If month or day are not there then it will still need the dashes.
	const output = year + "-" + month + "-" + day;

	return output;
}

export const handleDate = (
	date: string,
	locale: string,
	translate: TFunction,
	time?: boolean
) => {
	try {
		const momentDate = moment(date).locale(locale);

		if (!momentDate.isValid()) {
			return translate("main.dates.invalid");
		} else if (time) {
			return moment(momentDate).format("D MMM, YY HH:mm");
		} else {
			return moment(momentDate).format("YYYY-MM-DD");
		}
	} catch {
		return translate("main.dates.invalid");
	}
};

/**
 *
 * Returns string value of the date or the date with time e.g. (20/01/2022 or 20/01/2022 12:12)
 *
 * @param date
 * @param locale language
 * @param translate translate function
 * @param time
 * @returns date with time or just the date
 */
export const getDateTime = (
	date: string,
	locale: string,
	translate: TFunction,
	time = true
) => {
	try {
		const momentDate = moment(date).locale(locale);
		if (!momentDate.isValid()) return translate("main.dates.invalid");
		else if (time) return moment(momentDate).format("L HH:mm");
		else return moment(momentDate).format("L");
	} catch {
		return translate("main.dates.invalid");
	}
};

/**
 * Returns true/false depending if the day has past
 *
 * @param date
 * @param locale language
 * @returns boolean
 */
export const isDateOverdue = (date: string, locale: string) => {
	try {
		const momentDate = moment(date).locale(locale);
		if (moment(momentDate).isBefore()) return true;
		else return false;
	} catch {
		return false;
	}
};
